// Renders alert descriptions / brief markdown with tight Tailwind
// styling. Used in the inbox alert cards and the pending-approvals
// queue so Owen's content briefs (and any other markdown-rich agent
// output) render legibly instead of as a wall of raw markdown text.

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useNavigate } from "react-router-dom";

const DASHBOARD_HOSTS = new Set([
  "rankforge-dashboard.pages.dev",
  "localhost",
  "127.0.0.1",
]);

/** Convert in-app dashboard links to relative paths so they navigate
 *  via React Router (same-tab, SPA-aware) instead of forcing a new
 *  tab. Cross-origin links keep target="_blank". */
function normalizeHref(href: string): { internalPath: string | null; externalUrl: string | null } {
  if (!href) return { internalPath: null, externalUrl: null };
  if (href.startsWith("/")) return { internalPath: href, externalUrl: null };
  try {
    const u = new URL(href);
    if (DASHBOARD_HOSTS.has(u.hostname)) {
      return { internalPath: u.pathname + u.search + u.hash, externalUrl: null };
    }
    return { internalPath: null, externalUrl: href };
  } catch {
    // Relative path or invalid — treat as internal/relative.
    return { internalPath: href, externalUrl: null };
  }
}

export function MarkdownBody({ source, className = "" }: { source: string; className?: string }) {
  const navigate = useNavigate();
  return (
    <div className={"text-sm text-ink-700 leading-relaxed " + className}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Headings — small + tight.
          h1: ({ children }) => <h2 className="text-base font-display font-bold text-ink-900 mt-4 mb-2 first:mt-0">{children}</h2>,
          h2: ({ children }) => <h3 className="text-sm font-display font-bold uppercase tracking-wider text-ink-700 mt-4 mb-2 first:mt-0">{children}</h3>,
          h3: ({ children }) => <h4 className="text-xs font-display font-bold uppercase tracking-wider text-ink-600 mt-3 mb-1.5 first:mt-0">{children}</h4>,
          // Paragraphs.
          p: ({ children }) => <p className="my-2 first:mt-0 last:mb-0">{children}</p>,
          // Emphasis.
          strong: ({ children }) => <strong className="font-semibold text-ink-900">{children}</strong>,
          em: ({ children }) => <em className="italic text-ink-700">{children}</em>,
          // Lists — keep tight, slightly indented.
          ul: ({ children }) => <ul className="list-disc list-outside ml-5 my-2 space-y-0.5">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal list-outside ml-5 my-2 space-y-0.5">{children}</ol>,
          li: ({ children }) => <li className="text-ink-700">{children}</li>,
          // Blockquote.
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-accent/50 bg-ink-50 pl-3 pr-2 py-1.5 my-3 italic text-ink-700">
              {children}
            </blockquote>
          ),
          // Inline code + code blocks.
          code: ({ className: codeClass, children }) => {
            const isInline = !codeClass; // remark-gfm: block code carries language- class
            if (isInline) {
              return <code className="bg-ink-100 text-ink-800 px-1 py-0.5 rounded text-[12px] font-mono">{children}</code>;
            }
            return <code className={"font-mono text-[12px] " + (codeClass ?? "")}>{children}</code>;
          },
          pre: ({ children }) => (
            <pre className="bg-ink-50 border border-ink-100 rounded p-3 my-3 overflow-x-auto text-[12px]">{children}</pre>
          ),
          // Horizontal rule + link styling.
          hr: () => <hr className="my-4 border-ink-100" />,
          a: ({ href, children }) => {
            const { internalPath, externalUrl } = normalizeHref(href ?? "");
            if (internalPath) {
              // Dashboard-internal: navigate via useNavigate on click.
              // React Router's <Link to=...> can silently no-op when
              // the destination is same-path-different-query (only the
              // ?id changes), which is exactly our brief-link case.
              // Calling navigate() explicitly forces the searchParams
              // update + re-render reliably across all browsers.
              return (
                <a
                  href={internalPath}
                  className="text-accent hover:underline"
                  onClick={(e) => {
                    // Allow modifier-clicks (cmd/ctrl/middle) to open
                    // in a new tab the way the user expects from any
                    // <a href>; only intercept plain left-click.
                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
                    e.preventDefault();
                    navigate(internalPath);
                  }}
                >
                  {children}
                </a>
              );
            }
            return (
              <a
                href={externalUrl ?? href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent hover:underline"
              >
                {children}
              </a>
            );
          },
          // Images — constrain width to fit the alert pane.
          img: ({ src, alt }) => (
            <img
              src={src}
              alt={alt ?? ""}
              className="max-w-full h-auto rounded border border-ink-200 my-2 shadow-sm"
              loading="lazy"
            />
          ),
          // Tables — handled by remark-gfm, give them light borders.
          table: ({ children }) => (
            <div className="overflow-x-auto my-3">
              <table className="text-xs w-full border-collapse">{children}</table>
            </div>
          ),
          thead: ({ children }) => <thead className="bg-ink-50">{children}</thead>,
          th: ({ children }) => <th className="text-left px-2 py-1.5 border-b border-ink-200 text-ink-600 font-semibold uppercase tracking-wide text-[10px]">{children}</th>,
          td: ({ children }) => <td className="px-2 py-1.5 border-b border-ink-100 align-top">{children}</td>,
        }}
      >
        {source}
      </ReactMarkdown>
    </div>
  );
}
